import { getLayoutData, getPageData, getPages } from '@/pages/api/api';
import SingleNewsBanner from '@/components/singleNewsBanner/SingleNewsBanner';
import { FRAGMENTS } from '@repo/ui/lib';
import { GROUP_FRAGMENTS } from '@/lib';
import dynamic from 'next/dynamic';
import dynamicWithLoader from '@repo/ui/src/components/common/DynamicWithLoader';

const StructuredTextExtendedContent = dynamicWithLoader(
	() => import('@/components/structuredTextExtendedContent/StructuredTextExtendedContent'),
);

const StickyHeading = dynamic(() => import('@repo/ui/src/components/StickyHeading'), {
	ssr: false,
});

const NewsThumbnails = dynamicWithLoader(() => import('@repo/ui/src/components/newsThumbnails/NewsThumbnails'));

const CheckOthers = dynamicWithLoader(() => import('@repo/ui/src/components/CheckOthers'));

export default function Page({ data, locale }) {
	const content = data?.content || null;
	const authorData = data?.author || {};
	const newsThumbnailsData = data?.chooseOtherNews || [];
	const checkOthersData = data?.chooseCheckOthers || {};
	const preparedNewsThumbnailsData = {
		chooseNews: newsThumbnailsData,
		header: locale === 'pl' ? 'Zobacz inne wpisy' : 'Check other blog posts',
		paddings: {
			top: 'none',
			bottom: 'medium',
		},
	};
	return (
		<>
			<StickyHeading stickyHeadingsSelectors={[]} />
			<SingleNewsBanner data={data} />
			{content && <StructuredTextExtendedContent data={content} author={authorData} />}
			{newsThumbnailsData.length > 0 && <NewsThumbnails data={preparedNewsThumbnailsData} isCurrentPageGroup={true} />}
			{Object.keys(checkOthersData).length !== 0 && <CheckOthers data={checkOthersData} />}
		</>
	);
}

const PAGE_CONTENT_TYPE = 'allCommonBlogPages';

const pathsQuery = `
	query GetNewsPages($locale: SiteLocale) {
		${PAGE_CONTENT_TYPE}(locale: $locale, first: 100) {
            title
            blogPageName
            slug
		}
	}
`;

const propsQuery = `
	query GetNewsPagesData($slug: String, $locale: SiteLocale)  {
        ${PAGE_CONTENT_TYPE}(locale: $locale, filter: {slug: {eq: $slug}}) {
            title
            blogPageName
            slug
            ${FRAGMENTS.SEO_META_FRAGMENT}		
			${FRAGMENTS.THEME_FRAGMENT}
            _createdAt
            wordsCount
            author {
                __typename
                fullNameSurname
                jobTitle
                video {
                    ${FRAGMENTS.VIDEO_FRAGMENT}
                }
                smallImage {
                    ${FRAGMENTS.IMAGE_FRAGMENT}
                }
            }
            pleoCategory
            categories
            thumbnailImage {
                ${FRAGMENTS.IMAGE_FRAGMENT}
            }
            thumbnailImageHorizontal {
               ${FRAGMENTS.IMAGE_FRAGMENT}
            }
            content {
                value
                links {
                    __typename
                    ${FRAGMENTS.HORIZONTAL_HEADER_MODULE_FRAGMENT}
                    ${FRAGMENTS.SOCIAL_MEDIA_FRAGMENT}
                    
                    ${GROUP_FRAGMENTS.SINGLE_ASSET_FRAGMENT}
                    ${GROUP_FRAGMENTS.CHECK_OTHERS_FRAGMENT}
                }
                blocks {
                    __typename
                    ... on MarginRecord {
                        id
                        margin
                    }
                }
            }
            chooseOtherNews {
                title
                blogPageName
                slug
                _createdAt
                wordsCount
                ${FRAGMENTS.THEME_FRAGMENT}
                author {
                    __typename
                    fullNameSurname
                    jobTitle
                    video {
                        ${FRAGMENTS.VIDEO_FRAGMENT}
                    }
                    smallImage {
                        ${FRAGMENTS.IMAGE_FRAGMENT}
                    }
                }
                pleoCategory
                categories
                thumbnailImage {
                    ${FRAGMENTS.IMAGE_FRAGMENT}
                }
                thumbnailImageHorizontal {
                   ${FRAGMENTS.IMAGE_FRAGMENT}
                }
            }
            chooseCheckOthers {
                ${GROUP_FRAGMENTS.CHECK_OTHERS_FRAGMENT}
            }
        }
    }
`;

export async function getStaticProps({ params, locale }) {
	const { slug } = params;
	const data = await getPageData(PAGE_CONTENT_TYPE, slug, propsQuery, locale);
	const { globals, cookies, menu, footer } = await getLayoutData(locale);
	return {
		props: {
			cookies,
			data,
			locale,
			globals,
			footer,
			menu,
		},
		revalidate: 120,
	};
}

export async function getStaticPaths({ locales }) {
	const paths = [];
	for (const locale of locales) {
		const pages = await getPages(PAGE_CONTENT_TYPE, pathsQuery, locale);
		const newsPage = pages
			.map((page) => {
				return {
					params: {
						slug: page.slug,
					},
					locale,
				};
			})
			.filter((el) => {
				return el !== null;
			});
		paths.push(...newsPage);
	}
	return {
		paths,
		fallback: false,
	};
}
